<template>
  <div
    ref="richTextEditor"
    class="com_rich-text-container"
  >
    <label
      v-if="labelText !== ''"
      class="com_rich-text__label obj_form-label obj_form-label--spacing-bottom"
    >
      {{ labelText }}
    </label>

    <div
      data-testid="rich-text-editor"
      class="com_rich-text"
      :class="{
        'com_rich-text--upload-allowed': isUploadAllowed,
      }"
    >
      <input
        :id="`trix-input-${randomId}`"
        :value="modelValue"
        type="hidden"
        name="content"
        data-testid="trix-input-hidden"
      >

      <div
        class="com_rich-text__toolbar-wrapper"
      >
        <trix-toolbar
          :id="`custom_toolbar-${randomId}`"
          ref="trixToolbar"
          class="com_rich-text__toolbar"
        />

        <slot
          name="top"
          class="com_rich-text__top-action"
        />
      </div>

      <trix-editor
        ref="trix"
        :toolbar="`custom_toolbar-${randomId}`"
        class="com_rich-text__editor"
        :placeholder="placeholder"
        :input="`trix-input-${randomId}`"
        v-bind="isIPad
          ? {
            spellcheck: 'false',
            autocorrect: 'off',
            autocomplete: 'off',
          }
          : {}"
        @trix-paste="onTrixPaste"
        @trix-focus="onTrixFocus"
        @trix-initialize="onTrixInitialize"
        @trix-change="onChangeText"
        @trix-before-initialize="onTrixBeforeInitialize"
        @trix-selection-change="onTrixSelectionChange"
        @trix-attachment-add="onTrixAttachmentAdd"
        @trix-file-accept="onTrixFileAccept"
      />

      <slot
        name="bottom"
        class="com_rich-text__bottom-action"
      />

      <button
        v-if="isDictationModeSupported"
        type="button"
        class="com_rich-text__dictation-fab"
        data-testid="trix-dictation-fab"
        :title="$t('layout.dictation_mode_for_ipad')"
        :aria-label="$t('layout.dictation_mode_for_ipad')"
        @click="onClickDictationToggle"
      >
        <!-- eslint-disable vue/max-attributes-per-line -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />

          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />

          <line
            x1="12"
            x2="12"
            y1="19"
            y2="22"
          />
        </svg>
        <!-- eslint-enable vue/max-attributes-per-line -->
      </button>
    </div>

    <div
      class="com_rich-text__alert"
    >
      <MessageBoxError
        v-if="state === validStates.IMAGE_UPLOAD_SIZE_ERROR"
        data-testid="rich-text-editor-messageboxerror-image-size"
      >
        {{ $t('feature.account.user_signature.image_file_size_upload_error') }}
      </MessageBoxError>

      <MessageBoxError
        v-if="state === validStates.IMAGE_UPLOAD_ERROR"
        data-testid="rich-text-editor-messageboxerror-image-upload"
      >
        {{ $t('feature.account.user_signature.file_upload_error') }}
      </MessageBoxError>

      <MessageBoxError
        v-if="state === validStates.IMAGE_UPLOAD_TYPE_ERROR"
        data-testid="rich-text-editor-messageboxerror-image-type"
      >
        {{ $t('feature.account.user_signature.allowed_upload_file_types') }}
      </MessageBoxError>

      <MessageBoxInfo
        v-if="uploadType === 'signature'"
        data-testid="rich-text-editor-messageboxinfo-image-upload"
      >
        {{ $t('feature.account.user_signature.info_signature_image') }}
      </MessageBoxInfo>
    </div>

    <ModalDictation
      v-if="isDictationMode"
      @on:insert="onDictationInsert"
      @after:close-animation="isDictationMode = false"
    />
  </div>
</template>

<script>
import constants from '@client-shared/config/constants.js'
import createAwsS3Options from '@client-shared/utils/create-aws-s3-options.js'
import generateRandomId from '@client-shared/utils/crypto-random-uuid.js'
import extractDataUriImages from '@client-shared/utils/extract-data-uri-images.js'
import getErrorStatus from '@client-shared/utils/get-error-status.js'
import AwsS3 from '@uppy/aws-s3'
import { Uppy } from '@uppy/core'
import DOMPurify from 'dompurify'
import Trix from 'trix'
import { markRaw } from 'vue'

import 'trix/dist/trix.css'

import MessageBoxError from '@/components/MessageBoxError.vue'
import MessageBoxInfo from '@/components/MessageBoxInfo.vue'
import ModalDictation from '@/components/ModalDictation.vue'
import isAndroid from '@/utils/is-android.js'
import isIPadOs from '@/utils/is-i-pad-os'

// taken from https://github.com/basecamp/trix/blob/a572f21bab3b20a6bd002bb52b52ba8914acddsrc/trix/core/helpers/dom.js
const removeNode = (node) => node?.parentNode?.removeChild(node)

export default {
  name: 'RichTextEditor',

  components: {
    MessageBoxInfo,
    MessageBoxError,
    ModalDictation,
  },

  props: {
    uploadType: {
      type: String,
      default: '',

      validator: (value) => {
        return [
          '',
          'signature',
          'support',
        ].includes(value)
      },
    },

    labelText: {
      type: String,
      default: '',
    },

    modelValue: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    sanitizeAllowedTags: {
      type: Array,
      default: () => constants.SANITIZE_ALLOWED_TAGS.DEFAULT,
    },
  },

  emits: [
    'on-change',
    'on-selection-change',
  ],

  expose: [
    'blur',
    'overrideText',
  ],

  data () {
    const validStates = Object.freeze({
      INITIAL: 0,
      IMAGE_UPLOAD_PENDING: 1,
      IMAGE_UPLOAD_ERROR: 2,
      IMAGE_UPLOAD_SIZE_ERROR: 3,
      IMAGE_UPLOAD_TYPE_ERROR: 4,
    })

    return {
      state: validStates.INITIAL,
      validStates,
      randomId: generateRandomId(),
      uppy: null,
      trixAttachmentsByUppyFileId: null,
      trixHadFocus: false,
      isDictationMode: false,
      pendingOverrideText: null,
      dictationButton: null,
    }
  },

  computed: {
    isUploadAllowed () {
      return !!this.uploadType
    },

    isIPad () {
      return isIPadOs()
    },

    isDictationModeSupported () {
      return isIPadOs() || isAndroid()
    },
  },

  beforeUnmount () {
    if (this.isIPad && this.$refs.trix) {
      this.$refs.trix.removeEventListener('compositionstart', this.onCompositionStart)
    }

    if (this.dictationButton) {
      this.dictationButton.removeEventListener('click', this.onClickDictationToggle)
    }

    if (this.uppy) {
      this.uppy.destroy()
    }
  },

  created () {
    if (this.isUploadAllowed) {
      this.uppy = markRaw(new Uppy({
        autoProceed: true,
      }))
      // Trix attachments are DOM-bound and circular. Uppy 6 sends file.meta to Companion as JSON
      // (Uppy 5 stringified the values), so they are kept here by uppy file id instead of in meta.
      this.trixAttachmentsByUppyFileId = markRaw(new Map())

      this.uppy
        .use(AwsS3, createAwsS3Options({
          companionUrl: this.$config.api.companionUrl,
          uploadToken: this.$auth.user?.uploadToken,
        }))
        .on('upload-error', async (uppyFile, error) => {
          const responseStatusCode = getErrorStatus(error)

          if (responseStatusCode === 401 && this.$auth.loggedIn) {
            await this.$auth.logout()
          }

          this.state = this.validStates.IMAGE_UPLOAD_ERROR
          // Remove attachment from preview in error case
          // https://github.com/basecamp/trix#inserting-a-content-attachment
          this.trixAttachmentsByUppyFileId.get(uppyFile.id)
            .remove()
          this.trixAttachmentsByUppyFileId.delete(uppyFile.id)
        })
        .on('upload-progress', (uppyFile, progress) => {
          // Set upload progress on attachment
          const percentage = Math.round((progress.bytesUploaded / progress.bytesTotal) * 100)

          this.trixAttachmentsByUppyFileId.get(uppyFile.id)
            .setUploadProgress(percentage)
        })
        .on('file-added', () => {
          this.state = this.validStates.INITIAL
          this.trixHadFocus = true
        })
        .on('upload-success', (uppyFile, response) => {
          const key = response.body.key
          // Set specific attributes for Trix editor replacement element
          const attributes = {
            url: `${this.$config.awsS3BucketHost}/${key}`,
          }

          this.trixAttachmentsByUppyFileId.get(uppyFile.id)
            .setAttributes(attributes)
          this.trixAttachmentsByUppyFileId.delete(uppyFile.id)
          // Remove file from uppy after success
          this.uppy.removeFile(uppyFile.id)
        })
    }
  },

  methods: {
    onCompositionStart () {
      if (this.isDictationMode) {
        return
      }

      // Undo any text that the composition event may have started inserting
      this.$nextTick(() => {
        this.$refs.trix?.editor?.undo()
      })

      this.isDictationMode = true
    },

    onTrixInitialize () {
      if (this.pendingOverrideText) {
        const { text, options } = this.pendingOverrideText

        this.pendingOverrideText = null
        this.overrideText(text, options)
      }

      if (this.isIPad) {
        this.$refs.trix.addEventListener('compositionstart', this.onCompositionStart)
      }

      const trixToolbar = this.$refs.trixToolbar

      if (!trixToolbar) { // There are some scenarios where the toolbar is not in the DOM anymore (example: because of page transitions from the duplicate button in the task detail modal of the tasks map overlay)
        return
      }

      // Trix renders its toolbar markup itself, so its controls get their testids here
      const testIdsBySelector = {
        '[data-trix-attribute="bold"]': 'trix-button-bold',
        '[data-trix-attribute="italic"]': 'trix-button-italic',
        '[data-trix-attribute="href"]': 'trix-button-link',
        '[data-trix-attribute="bullet"]': 'trix-button-bullet-list',
        '[data-trix-attribute="number"]': 'trix-button-number-list',
        '[data-trix-dialog="href"] [data-trix-input]': 'trix-link-dialog-input',
        '[data-trix-dialog="href"] [data-trix-method="setAttribute"]': 'trix-link-dialog-submit',
      }

      for (const [
        selector,
        testId,
      ] of Object.entries(testIdsBySelector)) {
        trixToolbar.querySelector(selector)
          .setAttribute('data-testid', testId)
      }

      const button = document.createElement('button')

      button.setAttribute('type', 'button')
      button.setAttribute('class', 'trix-button trix-button--icon trix-button--icon-underline')
      button.setAttribute('data-trix-attribute', 'underline')
      button.setAttribute('title', 'underline')
      button.setAttribute('tabindex', '-1')
      button.setAttribute('data-testid', 'trix-custom-button-underline')
      button.textContent = 'U'

      // set shortcut
      // https://github.com/basecamp/trix/issues/643
      button.setAttribute('data-trix-key', 'u')

      // Attachment of the button to the toolBar
      const textToolBar = trixToolbar.querySelector('.trix-button-group--text-tools')
      const textToolBarButtonItalic = trixToolbar.querySelector('.trix-button--icon-italic')

      textToolBar.insertBefore(button, textToolBarButtonItalic.nextSibling)

      if (this.isDictationModeSupported) {
        // Drop the listener from a button created by a previous trix-initialize so listeners
        // don't accumulate when trix re-initializes on the same component instance.
        if (this.dictationButton) {
          this.dictationButton.removeEventListener('click', this.onClickDictationToggle)
        }

        // Dictation button — opens a plain textarea for system voice dictation (e.g. iPadOS, Android)
        const dictationButton = document.createElement('button')

        dictationButton.setAttribute('type', 'button')
        dictationButton.setAttribute('class', 'trix-button trix-button--dictation-button')
        dictationButton.setAttribute('title', this.$t('layout.dictation_mode_for_ipad'))
        dictationButton.setAttribute('tabindex', '-1')
        dictationButton.setAttribute('data-testid', 'trix-custom-button-dictation')
        dictationButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>'

        // Use the bound method reference (stable in Options API) so it can be removed again.
        dictationButton.addEventListener('click', this.onClickDictationToggle)
        this.dictationButton = dictationButton

        const blockToolBar = trixToolbar.querySelector('.trix-button-group--block-tools')

        blockToolBar.appendChild(dictationButton)
      }
    },

    onClickDictationToggle () {
      this.isDictationMode = true
    },

    onDictationInsert (text) {
      if (text) {
        this.trixHadFocus = true

        const editor = this.$refs.trix.editor

        const [ cursorPosition ] = editor.getSelectedRange()

        editor.insertString(text)
        editor.setSelectedRange(cursorPosition + text.length)

        // insertString updates the hidden input but may not trigger trix-change
        // reliably, so emit the change manually
        const sanitizedText = DOMPurify.sanitize(this.$refs.trix.value, {
          ALLOWED_TAGS: this.sanitizeAllowedTags,
        })

        this.$emit('on-change', sanitizedText)
      }
    },

    onTrixFocus () {
      this.trixHadFocus = true
    },

    onTrixFileAccept (e) {
      if (!this.isUploadAllowed) {
        e.preventDefault()
      }
    },

    onTrixPaste (data) {
      if (!data.paste.html) {
        return
      }

      this.$refs.trix.editor.setSelectedRange(data.paste.range)
      this.$refs.trix.editor.deleteInDirection('backward')

      const { html, files } = extractDataUriImages(DOMPurify.sanitize(data.paste.html, {
        ALLOWED_TAGS: this.sanitizeAllowedTags,
      }))

      this.$refs.trix.editor.insertHTML(html)

      if (!this.isUploadAllowed) {
        return
      }

      // Inline images are inserted as regular attachments, so onTrixAttachmentAdd validates type and size and uploads them.
      // They end up after the pasted text, because trix can only insert files at the cursor.
      for (const file of files) {
        this.$refs.trix.editor.insertFile(file)
      }
    },

    onChangeText (event) {
      if (this.trixHadFocus) {
        const sanitizedText = DOMPurify.sanitize(event.target.value, {
          ALLOWED_TAGS: this.sanitizeAllowedTags,
        })

        this.$emit('on-change', sanitizedText)
      }
    },

    onTrixSelectionChange () {
      // ensure trix editor is still loaded
      if (!this.$refs?.trix?.editor) {
        return
      }

      // Outputting the fragment content using a throwaway intermediary DOM element (div):
      const div = document.createElement('div')

      div.appendChild(window.getSelection()
        .getRangeAt(0)
        .cloneContents())

      this.$emit('on-selection-change', {
        selectedText: div.innerHTML,
        cursorPosition: this.$refs.trix.editor.getSelectedRange(),
      })
    },

    onTrixBeforeInitialize () {
      // Overwrite trix pickFiles method to restrict MIMETypes with file picker
      if (this.isUploadAllowed) {
        // disable captions and file size
        Trix.config.attachments.preview.caption.name = false
        Trix.config.attachments.preview.caption.size = false
        Trix.config.attachments.file.caption.size = false

        const fileInputId = Trix.config.input.fileInputId
        const allowedTypesList = this.$config.constants.SIGNATURE_IMAGE_ALLOWED_MIME_TYPES.toString()

        Trix.config.input.pickFiles = cb => {
          const input = document.createElement('input')

          input.setAttribute('id', fileInputId)
          input.setAttribute('type', 'file')
          input.setAttribute('hidden', 'true')
          input.setAttribute('multiple', 'true')
          input.setAttribute('accept', allowedTypesList)

          input.addEventListener('change', () => {
            cb(input.files)
            removeNode(input)
          })

          removeNode(document.getElementById(fileInputId))
          this.$refs.richTextEditor.appendChild(input) // Append it to the current richTextEditor instance, because adding it to the root would cause the modal to disappear on click (as the button would be placed outside of the modal). Check this issue for reference: https://3.basecamp.com/4480613/buckets/16502492/todos/6412906020
          input.click()
        }
      }

      // CONFIGURE TRIX EDITOR
      // Trix Editor Underline Button
      // https://github.com/basecamp/trix/pull/473
      Trix.config.textAttributes.underline = {
        tagName: 'u',
        inheritable: false,
        parser (e) {
          const style = window.getComputedStyle(e)

          return style.fontStyle === 'underline'
        },
      }
    },

    onTrixAttachmentAdd (e) {
      // https://github.com/basecamp/trix#storing-attached-files
      // https://github.com/basecamp/trix#inserting-a-file
      // https://trix-editor.org/js/attachments.js

      if (!this.isUploadAllowed) {
        return
      }

      const { attachment } = e

      // when copying picture from editor to editor the first fired event has no file
      if (!e.attachment.file) {
        return
      }

      const file = e.attachment.file

      if (!this.$config.constants.SIGNATURE_IMAGE_ALLOWED_MIME_TYPES.includes(file.type)) {
        attachment.remove()
        this.state = this.validStates.IMAGE_UPLOAD_TYPE_ERROR

        return
      }

      //  100kb file size limit
      if (this.uploadType === 'signature') {
        if (file.size > this.$config.constants.SIGNATURE_IMAGE_FILE_SIZE_LIMIT) {
          attachment.remove()
          this.state = this.validStates.IMAGE_UPLOAD_SIZE_ERROR

          return
        }
      }

      const generateDownloadFileName = originName => {
        const encoded = encodeURIComponent(originName)
        const nameArr = encoded.split('.')
        const extension = nameArr.pop()

        nameArr.push(`--${generateRandomId()}.${extension}`)

        return nameArr.join('')
      }

      const uploadFile = {
        name: generateDownloadFileName(file.name),
        type: file.type,
        data: file,
        meta: {
          userId: this.$auth.user._id,
          filesize: file.size,
          qqfilename: encodeURIComponent(file.name),
          isPublic: [
            'signature',
            'support',
          ].includes(this.uploadType),

          operation: this.uploadType,
        },
      }

      try {
        this.state = this.validStates.IMAGE_UPLOAD_PENDING

        // Registered before the upload starts: autoProceed schedules it asynchronously.
        const uppyFileId = this.uppy.addFile(uploadFile)

        this.trixAttachmentsByUppyFileId.set(uppyFileId, attachment)
      } catch (e) {
        console.error(e)

        this.uppy.destroy()
      } finally {
        this.state = this.validStates.INITIAL
      }
    },

    blur () {
      this.$refs.trix && this.$refs.trix.blur()
    },

    overrideText (text, options = {}) {
      if (!this.$refs.trix?.editor) { // trix sets the editor property asynchronously after mount — apply the call on trix-initialize instead
        this.pendingOverrideText = {
          text, options,
        }

        return
      }

      const shouldFocus = options.shouldFocus === undefined ? true : options.shouldFocus

      this.$refs.trix.editor.setSelectedRange([
        0,
        this.$refs.trix.editor.getDocument()
          .getLength(),
      ])
      this.$refs.trix.editor.insertHTML(text)

      if (!shouldFocus) {
        this.blur()
      }
    },
  },
}
</script>
